/**
 * Versioned, renderer-independent scene rules.
 *
 * History rows persist the outputs of these rules so replay does not change
 * when a future renderer tunes its animation. Keep this module free of
 * portfolio/history types so both live projections and history derivation can
 * use exactly the same formulas.
 */
export const SCENE_RULE_VERSION_V1 = 1 as const;
export const SCENE_V1_MIN_SLIME_SCALE = 0.6;
export const SCENE_V1_MAX_SLIME_SCALE = 1.9;

export type SceneWeatherV1 = "clear" | "party" | "storm" | "panic";
export type HistorySceneWeatherV1 = SceneWeatherV1 | "unknown";

export interface ScenePositionInputV1 {
  readonly marketValue: number;
  readonly dailyReturnPct: number | null;
  readonly returnUsable: boolean;
}

export interface SceneRoomSignalsV1 {
  readonly valence: number;
  readonly knownReturnPct: number;
  readonly dataCoverage: number;
  readonly partyIntensity: number;
  readonly stormIntensity: number;
  readonly panicIntensity: number;
  readonly weather: SceneWeatherV1;
  readonly totalMarketValue: number;
}

export function clampSceneValueV1(
  value: number,
  minimum: number,
  maximum: number,
): number {
  if (!Number.isFinite(value)) {
    return minimum;
  }
  return Math.min(maximum, Math.max(minimum, value));
}

export function usableSceneMarketValueV1(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

export function holdingMoodSignalV1(
  dailyReturnPct: number | null,
): number {
  if (dailyReturnPct === null || !Number.isFinite(dailyReturnPct)) {
    return 0;
  }
  return Math.tanh(dailyReturnPct / 4);
}

export function slimeScaleSignalV1(amountRatio: number): number {
  const safeRatio =
    Number.isFinite(amountRatio) && amountRatio > 0 ? amountRatio : 0;
  const rawScale = 0.65 + 0.35 * Math.log2(1 + safeRatio);
  return clampSceneValueV1(
    rawScale,
    SCENE_V1_MIN_SLIME_SCALE,
    SCENE_V1_MAX_SLIME_SCALE,
  );
}

export function panicIntensityFromStormIntensityV1(
  stormIntensity: number,
): number {
  return Math.max(0, (stormIntensity - 0.72) / 0.28);
}

export function deriveRoomSignalsV1(
  positions: readonly ScenePositionInputV1[],
): SceneRoomSignalsV1 {
  const totalMarketValue = positions.reduce(
    (sum, position) =>
      sum + usableSceneMarketValueV1(position.marketValue),
    0,
  );

  if (totalMarketValue <= 0) {
    return {
      valence: 0,
      knownReturnPct: 0,
      dataCoverage: 0,
      partyIntensity: 0,
      stormIntensity: 0,
      panicIntensity: 0,
      weather: "clear",
      totalMarketValue: 0,
    };
  }

  let dataCoverage = 0;
  let weightedReturnContribution = 0;
  for (const position of positions) {
    if (
      !position.returnUsable ||
      position.dailyReturnPct === null ||
      !Number.isFinite(position.dailyReturnPct)
    ) {
      continue;
    }
    const weight =
      usableSceneMarketValueV1(position.marketValue) / totalMarketValue;
    dataCoverage += weight;
    weightedReturnContribution += weight * position.dailyReturnPct;
  }

  const knownReturnPct =
    dataCoverage > 0 ? weightedReturnContribution / dataCoverage : 0;
  const valence =
    dataCoverage > 0
      ? clampSceneValueV1(
          dataCoverage * Math.tanh(knownReturnPct / 2.5),
          -1,
          1,
        )
      : 0;
  const partyIntensity = Math.max(valence, 0);
  const stormIntensity = Math.max(-valence, 0);
  const panicIntensity =
    panicIntensityFromStormIntensityV1(stormIntensity);

  const weather: SceneWeatherV1 =
    valence >= 0.55
      ? "party"
      : stormIntensity >= 0.85
        ? "panic"
        : valence < 0
          ? "storm"
          : "clear";

  return {
    valence,
    knownReturnPct,
    dataCoverage,
    partyIntensity,
    stormIntensity,
    panicIntensity,
    weather,
    totalMarketValue,
  };
}

/**
 * A stored `unknown` distinguishes "no return observation" from a measured
 * flat day. The live renderer intentionally falls back to the same clear room
 * it already uses for zero coverage.
 */
export function historyWeatherSignalV1(
  room: Pick<SceneRoomSignalsV1, "dataCoverage" | "weather">,
): HistorySceneWeatherV1 {
  return room.dataCoverage > 0 ? room.weather : "unknown";
}

export function renderWeatherForHistorySignalV1(
  weather: HistorySceneWeatherV1,
): SceneWeatherV1 {
  return weather === "unknown" ? "clear" : weather;
}
